import { LitElement, html, css } from '@hh/global.js'

class StoreLocator extends LitElement {
  constructor() {
    super()
    this._iconAdded = false
    this._initialized = false
    this._mapLoaded = false
  }

  connectedCallback() {
    super.connectedCallback()
    this.pollFunction(this.handleLoading.bind(this), 30000)

    // grab onto Storemappers search hooks/callbacks and add our own functionality
    window.storemapper_after_search = () => {
      this.restart()
    }

    window.storemapper_after_search_result = () => {
      this.restart()
    }
  }

  restart() {
    this.resetLoadedVars()
    this.setSidebar()
  }

  // A handful of things should only be done once, unless we change the search, so we need to be able to reset them all
  resetLoadedVars() {
    this._initialized = false
    this._mapLoaded = false
  }

  setSidebar() {
    // ! Filter Replacement
    // Remove any event handlers by replacing the elements outerHTML with itself
    this.querySelector('#storemapper-filter-drop-down-0').outerHTML =
      this.querySelector('#storemapper-filter-drop-down-0').outerHTML
    const filterDropdown = this.querySelector('#storemapper-filter-drop-down-0')

    // Select All Checkbox is a special case
    const selectAllCheckbox = filterDropdown.children[0].children[0]
    selectAllCheckbox.addEventListener('change', () => {
      // If they're selecting everything, add all the neccessary classes to the container
      const classesToToggle = [
        'filtering-active',
        'retail-salon-hidden',
        'certified-salon-hidden',
        'flagship-salon-hidden',
        'signature-salon-hidden',
      ]
      classesToToggle.forEach((classToToggle) => {
        this.querySelector('#store-mapper-container')?.classList.toggle(
          classToToggle,
          selectAllCheckbox.checked
        )
      })

      for (let i = 1; i < filterDropdown.children.length; i++) {
        if (selectAllCheckbox.checked) {
          // Then make sure all the other boxes are actually checked
          const filter = filterDropdown.children[i]
          const filterCheckbox = filter.children[0]
          filterCheckbox.checked = true
        } else {
          // Then uncheck the other boxes
          const filter = filterDropdown.children[i]
          const filterCheckbox = filter.children[0]
          filterCheckbox.checked = false
        }
      }
    })

    // For the standard filters
    for (let i = 1; i < filterDropdown.children.length; i++) {
      const filter = filterDropdown.children[i]
      const filterCheckbox = filter.children[0]
      filterCheckbox.addEventListener('change', () => {
        // Convert the labels text from Normal Case to spear-case
        const filterType = `${filterCheckbox.labels[0].innerText.replace(/\s+/g, '-').toLowerCase()}-hidden`
        // If the checkbox is checked, add "filtering-active" and filterType as classes to #store-mapper-container
        if (filterCheckbox.checked) {
          this.querySelector('#store-mapper-container').classList.add(
            'filtering-active',
            filterType
          )
        } else {
          // If the checkbox is unchecked, remove filterType as a class from #store-mapper-container
          this.querySelector('#store-mapper-container').classList.remove(
            filterType
          )
          // If no other checkboxes are checked, remove "filtering-active" from #store-mapper-container
          let anyChecked = false
          for (let j = 1; j < filterDropdown.children.length; j++) {
            const filter = filterDropdown.children[j]
            const filterCheckbox = filter.children[0]
            if (filterCheckbox.checked) {
              anyChecked = true
            }
          }
          if (!anyChecked) {
            this.querySelector('#store-mapper-container').classList.remove(
              'filtering-active'
            )
          }
        }
      })
    }

    const allFilter = filterDropdown.children[0]
    const flagFilter = filterDropdown.children[2]
    const sigFilter = filterDropdown.children[4]
    const certFilter = filterDropdown.children[1]
    const retailFilter = filterDropdown.children[3]

    filterDropdown.innerHTML = ''
    filterDropdown.appendChild(allFilter)
    filterDropdown.appendChild(flagFilter)
    filterDropdown.appendChild(sigFilter)
    filterDropdown.appendChild(certFilter)
    filterDropdown.appendChild(retailFilter)

    // ! Certified Icon Additions
    // Add the certified icon. Only needed for three of the store types
    const addCertifiedIcon = (store) => {
      if (!this._iconAdded) {
        // Create a flexbox containing the image
        const certImg = document.createElement('img')
        certImg.className = 'certified-stylist-icon stylist-sidebar'
        const flexDiv = document.createElement('div')
        flexDiv.className = 'flex'
        flexDiv.appendChild(certImg)

        // add the flexbox div that has the certified icon in it to the store
        store.children[0].insertBefore(flexDiv, store.children[0].children[2])
        // move the store name into the flexbox
        store.children[0].children[2].appendChild(store.children[0].children[1])
        // switch the order of elements, so that the name is actually first
        store.children[0].children[1].appendChild(
          store.children[0].children[1].children[0]
        )
      }
    }

    // ! Sidebar / Marker Class additions
    const markerObject =
      typeof Storemapper.markers != 'undefined'
        ? Storemapper.markers
        : Storemapper.stores.features
    for (let store of this.querySelector('#storemapper-list').children) {
      // Set the type of store, and add the certified icon where applicable
      // Also sets the markers to be slightly more efficient on first launch
      let salonType = undefined
      const description =
        Storemapper.stores.features[store.getAttribute('data-idx')].properties
          .description
      const storeCategoryID =
        Storemapper.stores.features[store.getAttribute('data-idx')].properties
          ?.store_category_tags[0]?.category_tag_id ?? undefined
      if (
        description.toLocaleLowerCase().includes('ouidad certified salon') ||
        storeCategoryID == 7714
      ) {
        salonType = 'certified-salon'
        addCertifiedIcon(store)
        markerObject[store.getAttribute('data-idx')]._element.classList.add(
          'certified-salon-marker'
        )
      } else if (
        description
          .toLocaleLowerCase()
          .includes('flagship salon' || storeCategoryID == 3027)
      ) {
        salonType = 'flagship-salon'
        addCertifiedIcon(store)
        markerObject[store.getAttribute('data-idx')]._element.classList.add(
          'flagship-salon-marker'
        )
      } else if (
        description
          .toLocaleLowerCase()
          .includes('signature salon' || storeCategoryID == 18008)
      ) {
        salonType = 'signature-salon'
        addCertifiedIcon(store)
        markerObject[store.getAttribute('data-idx')]._element.classList.add(
          'signature-salon-marker'
        )
      } else {
        // Assume anything else is a retail salon
        salonType = 'retail-salon'
        markerObject[store.getAttribute('data-idx')]._element.classList.add(
          'retail-salon-marker'
        )
      }

      store.classList.add(salonType)
      // ! Clean up the sidebar cards
      // Hide the website fields and descriptions
      store.children[0].children[7].classList.add('strmpr-hidden')
      store.children[0].children[8].classList.add('strmpr-hidden')
      // Also distance, because that sometimes shows up after searching?
      store.children[0].children[3].classList.add('strmpr-hidden')

      // Add the section showing the type of salon
      const storeType = salonType.replace(/-/g, ' ')
      const storeTypeCapitalized = storeType.replace(/\b\w/g, (l) =>
        l.toUpperCase()
      )
      const storeTypePanelTemplate = `<div class="store-type-panel flex">
        <img class="${salonType}-marker" />
        <p class="store-type-label">${storeTypeCapitalized}</p>
      </div>`
      const storeTypePanel = document.createElement('template')
      storeTypePanel.innerHTML = storeTypePanelTemplate
      store.children[0].insertBefore(
        storeTypePanel.content,
        store.children[0].children[9]
      )

      // Replace the Certified Stylists section with a custom one
      const stylistText = store.children[0].children[10].innerHTML
        .replace(/ , /g, ', ')
        .replace('Certified Stylist(s): ', '')
      const noStylists = stylistText.trim() === ''
      const certifiedStylistsTemplate = `
      <div class="certified-stylists-panel ${noStylists ? 'strmpr-hidden' : ''}">
        <p class="certified-stylists-text">Certified Stylist(s) </p>
        <div class="strmpr-field-custom storemapper-custom certified-stylists-list" aria-label="custom-field">
          ${store.children[0].children[10].innerHTML.replace("Certified Stylist(s): ", "").replace(/ , /g, ", ")}
        </div>
      </div>`

      const certifiedStylistsPanel = document.createElement('template')
      certifiedStylistsPanel.innerHTML = certifiedStylistsTemplate
      store.children[0].replaceChild(
        certifiedStylistsPanel.content,
        store.children[0].children[10]
      )

      // Add in the Educator Panel
      const educatorPanelTemplate = `<div class="educator-panel ${store.children[0].children[11].classList.contains('strmpr-hidden') ? 'strmpr-hidden' : ''}">
        <div class="educator-text flex">
          Educator(s) <img class="scissors">
        </div>
        <div class="strmpr-field-custom storemapper-custom educator-list ${store.children[0].children[11].classList.contains('strmpr-hidden') ? 'strmpr-hidden' : ''}" aria-label="custom-field">
          ${store.children[0].children[11].innerHTML.replace('Educators: ', '').replace(/ , /g, ', ').replace('Certified Stylist(s): ', '')}
        </div>
      </div>`
      const educatorPanel = document.createElement('template')
      educatorPanel.innerHTML = educatorPanelTemplate
      store.children[0].replaceChild(
        educatorPanel.content,
        store.children[0].children[11]
      )

      if ( !store.children[0].children[11].classList.contains('strmpr-hidden') ){
        store.classList.add('educator-salon')
        markerObject[store.getAttribute('data-idx')]._element.classList.add(
          'educator-salon-marker'
        )
      }

      // Add a click handler to the sidebar and markers to unhide the stylist icon if applicable
      store.children[2].addEventListener('click', (ev) => {
        setTimeout(() => {
          // If this store isn't a retail-salon, then it has a stylist icon
          if (!store.classList.contains('retail-salon')) {
            this.querySelector('#popup-certified-icon')?.classList?.remove(
              'hidden'
            )
          }
        }, 50)
      })

      markerObject[store.getAttribute('data-idx')]._element.addEventListener(
        'click',
        (ev) => {
          setTimeout(() => {
            // If this store isn't a retail-salon, then it has a stylist icon
            if (!store.classList.contains('retail-salon')) {
              this.querySelector('#popup-certified-icon').classList?.remove(
                'hidden'
              )
            }
          }, 50)
        }
      )
      // When you click anywhere on the store, it clicks the built in focus button
      store.addEventListener('click', (ev) => {
        store.children[2].click()
      })
    }
    this._iconAdded = true
  }

  // Just keep checking every 500ms if Storemapper has loaded properly, when it has, run startup(), otherwise check again
  handleLoading() {
    if (
      typeof Storemapper !== 'undefined' &&
      !this._mapLoaded &&
      typeof Storemapper?.map != 'undefined' &&
      Storemapper?.map._fullyLoaded &&
      typeof Storemapper?.stores != 'undefined'
    ) {
      this.startup()
      this._mapLoaded = true
      console.log('Store Locator Loaded.')
    } else {
      console.log('Loading Store Locator...')
    }
  }

  pollFunction(fn, timeout, interval = 500) {
    let canPoll = true
    let startTime = new Date().getTime()

    ;(function p() {
      canPoll = new Date().getTime() - startTime <= timeout
      if (!fn() && canPoll) {
        setTimeout(p, interval)
      }
    })()
  }

  sidebarTemplateModifications() {
    // ! V2 Sidebar Changes
    const sidebarTemplate = this.querySelector('#storemapper-list-template')
      .content.children[0]

    // Add the certified stylist icon next to the name
    const nameSection = `<div class="flex">
      <div class="flex text-flex">
        <div class="strmpr-field-name" aria-label="Location Name">~~STORE_NAME~~</div>
      </div>
      <img class="certified-stylist-icon">
    </div>`
    const nameTemplate = document.createElement('template')
    nameTemplate.innerHTML = nameSection
    sidebarTemplate.children[0].replaceChild(
      nameTemplate.content,
      sidebarTemplate.children[0].children[1]
    )
  }

  popuptemplateModifications() {
    // ! Popup Changes
    // Modify the template for the popup
    const popupTemplate = this.querySelector('#storemapper-popup-template')
      .content.children[0]
    // Hide the unused fields
    popupTemplate.children[5].classList.add('strmpr-hidden')
    popupTemplate.children[6].classList.add('strmpr-hidden')
    popupTemplate.children[7].classList.add('strmpr-hidden')
    popupTemplate.children[8].classList.add('strmpr-hidden')
    popupTemplate.children[9].classList.add('strmpr-hidden')

    // Add the certified stylist icon next to the name
    const nameSection = `<div class="flex">
      <div class="flex text-flex">
          <div class="strmpr-field-name" aria-label="Location Name">~~STORE_NAME~~</div>
      </div>
      <img class="certified-stylist-icon hidden" id="popup-certified-icon">
    </div>`
    const nameTemplate = document.createElement('template')
    nameTemplate.innerHTML = nameSection
    popupTemplate.replaceChild(nameTemplate.content, popupTemplate.children[1])

    // Move the address into a div and p tag to make styling easier
    const addressTextSection = `<div class="strmpr-field-address" aria-label="Location Address">
      <p class="address-text">~~STORE_ADDRESS~~</p>
    </div>`
    const addressTextTemplate = document.createElement('template')
    addressTextTemplate.innerHTML = addressTextSection
    popupTemplate.replaceChild(
      addressTextTemplate.content,
      popupTemplate.children[2]
    )

    // Add the directions section
    const directionSection = `<div class="strmpr-field-directions-text">
      <a href="~~DIRECTIONS_URL~~" target="_blank" aria-label="Location Directions URL, Opens in a New Window">Directions</a>
    </div>`
    const directionTemplate = document.createElement('template')
    directionTemplate.innerHTML = directionSection
    popupTemplate.insertBefore(
      directionTemplate.content,
      popupTemplate.children[3]
    )

    // make a div with class popup-body, move all children of popupTemplate into it, then put it back into popupTemplate
    const popupBodyDiv = document.createElement('div')
    popupBodyDiv.classList.add('popup-body')
    while (popupTemplate.children.length > 0) {
      popupBodyDiv.appendChild(popupTemplate.children[0])
    }
    popupTemplate.appendChild(popupBodyDiv)
  }

  startup() {
    this.sidebarTemplateModifications()

    this.setSidebar()

    this.popuptemplateModifications()

    // Make the reset button also modify the sidebar/markers
    if (!this.querySelector('.storemapper-reset').dataset.bound) {
      this.querySelector('.storemapper-reset').addEventListener(
        'mouseup',
        () => {
          this.resetLoadedVars()
          setTimeout((fn) => {
            this.setSidebar()
          }, 500)
        }
      )

      this.querySelector('.storemapper-reset').dataset.bound = true
    }

    // Once all our setup is done, we can show the container again
    this.querySelector('#store-mapper-container').classList.remove('opaque')
  }

  render() {
    return html` <slot></slot> `
  }
}
customElements.define('store-locator', StoreLocator)
